/**
 * UseChatSession — the behavior of the PRATUS AI chat, lifted out of the legacy
 * `ChatDisasterGPT.js` render tree so the V2 drawer can be pure presentation.
 * State still lives on `dchatSlice` (poll/post/resume/cancel) and the request
 * payload is byte-for-byte what the legacy shell sends: optimistic message,
 * legend-class filters, layer prefetch + ArcGIS user tokens, and the compacted
 * history (`compactDisasterChatPayload`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  cancelRun,
  resumeDChat,
  setCurrentSitrepId,
  setSelectedDChat,
  setStatus,
} from 'slices/dchatSlice';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/useOptionalUscgMapContext';
import { useIsCommandCenter } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/isCommandCenter';
import { useChatSidebar } from 'components/GlobalChatSidebar/ChatSidebarProvider';
import { selectGroupGuid, selectIncidentGuid } from 'slices/commonSelectors';
import { useQueryClient } from '@tanstack/react-query';
import {
  MAX_CHAT_ATTACHMENTS,
  chatFileIndexState,
  isChatImageAttachment,
  isChatImageFile,
  uploadChatFile,
  type ChatAttachment,
  type ChatFileIndexState,
  type PendingChatAttachment,
} from './chatAttachments';
import {
  fileProcessingStatusKeys,
  useFileProcessingStatus,
} from 'components/FileManagement/hooks/useFileProcessingStatus';
import { observability } from 'utils/observability';
import { useHighSecurityFlag } from 'utils/featureFlags';
import { getExtentFromGeoJson } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/getExtentFromGeoJson';
import {
  filesOrFoldersKeys,
  useGetAllFiles,
} from 'components/FileManagement/hooks/useGetAllFiles';
import {
  MAP_AREA_SOURCE,
  aorFolderFileIds,
  defaultHubRealtimeSelections,
  getMapAreaSelection,
  upsertMapAreaSelection,
  withDefaultFiles,
} from './dataSourceSelections';

import {
  useDeleteDisasterChat,
  usePollDChat,
  usePostDChat,
  useStopPollDChat,
} from '../hooks/useDisasterChats';
import { useAICommands, type AICommand } from '../hooks/useAICommands';
import {
  prefetchLayerFeaturesForAI,
  withLegendClassFiltersForAI,
} from '../utils/prefetchLayerFeaturesForAI';
import { compactDisasterChatPayload } from '../utils/layerSnapshotCompact';
import { withArcGisUserTokensForAI } from '../utils/arcGisUserTokenForAI';
import {
  applyDrawnAreaToLayersSelection,
  upsertSelectedLayerForAI,
} from '../utils/addSelectedLayerForAI';
import { expandSlashCommand } from '../utils/expandSlashCommand';
import type { ResumeDecision } from '../agentConfirm/types';
import {
  SERVER_IN_PROGRESS_STATUSES,
  type AgentMode,
  type ChatBlueprint,
  type ChatMessage,
  type DChat,
  type DChatClientStatus,
  type RealtimeSelection,
} from './types';
import { areSelectionsValid } from './chatHelpers';
import { useAiWorkspaceIncident } from './useAiWorkspaceIncident';

interface DchatSliceState {
  dchat: {
    dchat: DChat;
    status: DChatClientStatus;
    streamtext: string;
  };
}

/**
 * `dchatSlice` is untyped JS whose thunks resolve to values (resumeDChat →
 * boolean). Narrow `useDispatch` so those results are usable without `any`.
 */
type ThunkLike<R> = (dispatch: unknown, getState: unknown) => R;
interface AppThunkDispatch {
  <R>(thunk: ThunkLike<R>): R;
  (action: { type: string; payload?: unknown }): void;
}

export interface UseChatSessionOptions {
  /** Datetime window forwarded with each message (legacy prop passthrough). */
  selectedDatetime?: string;
  /** Called after the session is closed/deleted so the shell can hide itself. */
  onClose: () => void;
}

export interface ChatSession {
  dchat: DChat;
  messages: ChatMessage[];
  status: DChatClientStatus;
  streamtext: string;
  /** True while the agent is running (loading or ongoing). */
  isAgentRunning: boolean;
  /** Last server-side stage label shown while running. */
  priorStatus: string;
  /** Timestamp for optimistic messages of a not-yet-persisted chat. */
  sendMessageTimestamp?: Date;

  input: string;
  setInput: (value: string) => void;
  /** Bumps when a queued composer prompt is applied so the textarea can focus. */
  composerFocusNonce: number;
  submit: () => void;
  canSubmit: boolean;
  /** Files picked in the composer for the next message, uploaded to Org Files. */
  attachments: PendingChatAttachment[];
  /**
   * Indexing state per pending attachment id; null for images and files still
   * uploading.
   */
  attachmentIndexStates: Record<string, ChatFileIndexState | null>;
  addFiles: (files: File[]) => void;
  removeAttachment: (id: string) => void;
  /** Workspace the chat images are stored under, for loading thumbnails. */
  groupGuid?: string;
  incidentId?: string;
  /** False when a selected source still needs configuration (e.g. SITREP id). */
  selectionsValid: boolean;

  mode: AgentMode;
  setMode: (mode: AgentMode) => void;
  commands: AICommand[];

  realtimeSelections: RealtimeSelection[];
  setRealtimeSelections: React.Dispatch<
    React.SetStateAction<RealtimeSelection[]>
  >;

  aiLayerPickMode: boolean;
  /** Undefined when the drawer is rendered outside a map page. */
  toggleAiLayerPickMode?: () => void;
  /** True while the user is drawing an area for PRATUS AI to consider. */
  aiAreaSelectActive: boolean;
  /** Undefined when the drawer is rendered outside a map page. */
  startAiAreaSelect?: () => void;
  /** Zoom the map to the selected area, if any. */
  zoomToMapArea?: () => void;

  sendMessage: (
    message: string,
    newChatName?: string,
    prePromptData?: RealtimeSelection[] | null,
    attachments?: ChatAttachment[]
  ) => Promise<void>;
  redoMessage: (assistantMessageIndex: number) => void;
  cancel: () => void;
  deleteSession: () => void;
  close: () => void;

  pendingConfirmations: NonNullable<DChat['pending_confirmation']>;
  confirmationAskText?: string;
  submitConfirmations: (decisions: ResumeDecision[]) => Promise<boolean>;
}

function revokePreview(item: PendingChatAttachment): void {
  if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
}

function uploadErrorMessage(name: string, error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 403) {
    return `${name}: you do not have permission to upload to Org Files.`;
  }
  if (status === 413) return `${name}: the file is too large.`;
  return error instanceof Error && error.message
    ? `${name}: ${error.message}`
    : `${name} could not be uploaded.`;
}

const LAYER_SOURCES = new Set(['Layers', MAP_AREA_SOURCE]);
const MAP_AREA_GEOFENCE_OWNER = 'ai-chat-area';

const hasLayers = (sel: RealtimeSelection | null | undefined) =>
  !!sel &&
  LAYER_SOURCES.has(sel.source) &&
  Array.isArray(sel.selectedLayers) &&
  sel.selectedLayers.length > 0;

export const useChatSession = ({
  selectedDatetime,
  onClose,
}: UseChatSessionOptions): ChatSession => {
  const dispatch = useDispatch() as unknown as AppThunkDispatch;

  const [input, setInput] = useState('');
  const [composerFocusNonce, setComposerFocusNonce] = useState(0);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sendMessageTimestamp, setSendMessageTimestamp] = useState<Date>();
  const [realtimeSelections, setRealtimeSelections] = useState<
    RealtimeSelection[]
  >([]);
  const [priorStatus, setPriorStatus] = useState('');
  const [mode, setMode] = useState<AgentMode>('agent_confirm');
  const hubDefaultsSeededRef = useRef(false);
  /** Incident whose AOR folders were last seeded into the Files source. */
  const aorFilesSeededForRef = useRef<string | null>(null);

  const isHub = useIsCommandCenter();
  const highSecurity = useHighSecurityFlag();
  const incident = useAiWorkspaceIncident();
  const rawDchat = useSelector((s: DchatSliceState) => s.dchat.dchat);
  const dchat = useMemo<DChat>(() => rawDchat ?? {}, [rawDchat]);
  const status = useSelector((s: DchatSliceState) => s.dchat.status);
  const streamtext = useSelector((s: DchatSliceState) => s.dchat.streamtext);

  const isAgentRunning = status === 'loading' || status === 'ongoing';

  const pollDChat = usePollDChat();
  const stopPollDChat = useStopPollDChat();
  const postDChat = usePostDChat();
  const deleteDisasterChat = useDeleteDisasterChat();
  const commands = useAICommands(mode);

  const sharepointDriveId = incident?.sharepoint_location;
  const tenantId = incident?.tenant_id;

  const mapCtx = useOptionalUscgMapContext();
  const {
    aiPickMode,
    setAiPickMode,
    queuedComposerPrompt,
    consumeQueuedComposerPrompt,
    notifyComposerSend,
  } = useChatSidebar();
  const incidentGuid = useSelector(selectIncidentGuid) as string | undefined;
  const groupGuid = useSelector(selectGroupGuid) as string | undefined;
  const incidentAorIds = incident?.areas_of_responsibility;
  const orgFiles = useGetAllFiles(
    incident && incidentAorIds?.length ? groupGuid : undefined,
    'organization'
  );
  const aorFileIds = useMemo(
    () =>
      orgFiles.data
        ? aorFolderFileIds(orgFiles.data.dataset, incidentAorIds ?? [])
        : null,
    [orgFiles.data, incidentAorIds]
  );
  const [attachments, setAttachments] = useState<PendingChatAttachment[]>([]);
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  useEffect(() => () => attachmentsRef.current.forEach(revokePreview), []);

  const removeAttachment = useCallback((id: string) => {
    setAttachments((current) => {
      current
        .filter((item) => item.id === id)
        .forEach((item) => revokePreview(item));
      return current.filter((item) => item.id !== id);
    });
  }, []);

  const queryClient = useQueryClient();
  const indexingPending = attachments.some(
    (item) =>
      item.status === 'ready' &&
      !item.ragReady &&
      item.attachment &&
      !isChatImageAttachment(item.attachment)
  );
  const orgFileProcessing = useFileProcessingStatus(
    groupGuid,
    'organization',
    indexingPending
  );
  const attachmentIndexStates = useMemo(
    () =>
      Object.fromEntries(
        attachments.map((item) => [
          item.id,
          chatFileIndexState(item, orgFileProcessing.data),
        ])
      ),
    [attachments, orgFileProcessing.data]
  );

  const addFiles = useCallback(
    (files: File[]) => {
      if (!groupGuid) return;
      const room = MAX_CHAT_ATTACHMENTS - attachmentsRef.current.length;
      if (files.length > room) {
        toast.warn(`Upload up to ${MAX_CHAT_ATTACHMENTS} files per message.`);
      }
      files.slice(0, Math.max(0, room)).forEach((file) => {
        const id = crypto.randomUUID();
        setAttachments((current) => [
          ...current,
          {
            id,
            name: file.name,
            previewUrl: isChatImageFile(file)
              ? URL.createObjectURL(file)
              : undefined,
            status: 'uploading',
          },
        ]);
        uploadChatFile({ file, groupGuid })
          .then(({ attachment, ragReady }) => {
            setAttachments((current) =>
              current.map((item) =>
                item.id === id
                  ? { ...item, status: 'ready', attachment, ragReady }
                  : item
              )
            );
            if (attachment.fileId && !isChatImageAttachment(attachment)) {
              const fileId = attachment.fileId;
              setRealtimeSelections((current) =>
                withDefaultFiles(current, [fileId])
              );
            }
            void queryClient.invalidateQueries({
              queryKey: filesOrFoldersKeys.allFiles(groupGuid),
            });
            void queryClient.invalidateQueries({
              queryKey: fileProcessingStatusKeys.byWorkspace(groupGuid),
            });
          })
          .catch((error: unknown) => {
            toast.error(uploadErrorMessage(file.name, error));
            setAttachments((current) =>
              current.map((item) =>
                item.id === id ? { ...item, status: 'error' } : item
              )
            );
          });
      });
    },
    [groupGuid, queryClient]
  );
  const externalLayerTimeFilterById = mapCtx?.externalLayerTimeFilterById;
  const getAddedExternalLayers = mapCtx?.getAddedExternalLayers;
  const legendClassFilterByLayerId = mapCtx?.legendClassFilterByLayerId;
  const aiLayerPickMode = (mapCtx?.aiLayerPickMode ?? false) || aiPickMode;
  const setAiLayerPickMode = mapCtx?.setAiLayerPickMode;
  const registerAiLayerPickHandler = mapCtx?.registerAiLayerPickHandler;
  const registerAiDrawnAreaHandler = mapCtx?.registerAiDrawnAreaHandler;
  const getVisibleLayersForAI = mapCtx?.getVisibleLayersForAI;

  // Map pick → Layers selection (same toasts as legacy).
  useEffect(() => {
    if (!registerAiLayerPickHandler) return undefined;
    registerAiLayerPickHandler((payload) => {
      setRealtimeSelections((prev) => {
        const { next, added } = upsertSelectedLayerForAI(
          prev as never,
          payload,
          externalLayerTimeFilterById ?? {}
        );
        if (added) {
          const filterNote = payload.spatialFilterLabel
            ? ` (${payload.spatialFilterLabel})`
            : payload.legendFilterLabel
              ? ` (${payload.legendFilterLabel})`
              : '';
          toast.success(`Added ${payload.name}${filterNote} to Layers`);
        } else {
          toast.info(`${payload.name} is already selected`);
        }
        return next as RealtimeSelection[];
      });
    });
    return () => registerAiLayerPickHandler(null);
  }, [registerAiLayerPickHandler, externalLayerTimeFilterById]);

  // Select Area: the next drawn area becomes the Map Area context, not a Layers filter.
  const areaSelectIntentRef = useRef(false);
  const [aiAreaSelectActive, setAiAreaSelectActive] = useState(false);
  const cancelAiAreaDraw = mapCtx?.cancelAiAreaDraw;
  const registerAiDrawnAreaCancelHandler =
    mapCtx?.registerAiDrawnAreaCancelHandler;

  const endAreaSelect = useCallback(() => {
    if (!areaSelectIntentRef.current) return;
    areaSelectIntentRef.current = false;
    registerAiDrawnAreaCancelHandler?.(null);
    setAiAreaSelectActive(false);
    setAiLayerPickMode?.(false);
  }, [registerAiDrawnAreaCancelHandler, setAiLayerPickMode]);

  useEffect(() => {
    if (!registerAiDrawnAreaHandler || !getVisibleLayersForAI) return undefined;
    registerAiDrawnAreaHandler((payload) => {
      if (areaSelectIntentRef.current) {
        const visibleLayers = getVisibleLayersForAI();
        endAreaSelect();
        setRealtimeSelections((prev) =>
          upsertMapAreaSelection(
            prev,
            payload,
            visibleLayers,
            externalLayerTimeFilterById ?? {}
          )
        );
        toast.success('Added map area to PRATUS AI');
        return;
      }
      setRealtimeSelections((prev) => {
        const { next, added } = applyDrawnAreaToLayersSelection(
          prev as never,
          payload,
          getVisibleLayersForAI(),
          externalLayerTimeFilterById ?? {}
        );
        if (added) {
          toast.success('Added map area to Layers');
        } else {
          toast.info('Turn on a map layer to scope a drawn area');
        }
        return next as RealtimeSelection[];
      });
    });
    return () => registerAiDrawnAreaHandler(null);
  }, [
    registerAiDrawnAreaHandler,
    externalLayerTimeFilterById,
    getVisibleLayersForAI,
    endAreaSelect,
  ]);

  useEffect(() => {
    if (!aiLayerPickMode && !aiAreaSelectActive) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (areaSelectIntentRef.current) {
          cancelAiAreaDraw?.();
          endAreaSelect();
        }
        setAiPickMode(false);
        setAiLayerPickMode?.(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    aiAreaSelectActive,
    aiLayerPickMode,
    cancelAiAreaDraw,
    endAreaSelect,
    setAiLayerPickMode,
    setAiPickMode,
  ]);

  const startAiAreaSelect = useMemo(() => {
    if (!mapCtx?.startAiAreaDraw) return undefined;
    return () => {
      setAiPickMode(false);
      areaSelectIntentRef.current = true;
      setAiAreaSelectActive(true);
      mapCtx.setIsMapVisible(true);
      mapCtx.setAiLayerPickMode(true);
      mapCtx.registerAiDrawnAreaCancelHandler?.(() => endAreaSelect());
      mapCtx.startAiAreaDraw();
      toast.info('Draw an area on the map — Esc to cancel');
    };
  }, [endAreaSelect, mapCtx, setAiPickMode]);

  // Outline the selected area on the map while it is part of the context.
  const mapAreaFilter = getMapAreaSelection(realtimeSelections)?.spatialFilter;
  const showReportGeofence = mapCtx?.showReportGeofence;
  const clearReportGeofence = mapCtx?.clearReportGeofence;
  useEffect(() => {
    if (!mapAreaFilter || !showReportGeofence || !clearReportGeofence) {
      return undefined;
    }
    showReportGeofence(
      mapAreaFilter as never,
      'PRATUS AI area',
      MAP_AREA_GEOFENCE_OWNER
    );
    return () => clearReportGeofence(MAP_AREA_GEOFENCE_OWNER);
  }, [clearReportGeofence, mapAreaFilter, showReportGeofence]);

  const mapView = mapCtx?.mapView;
  const setIsMapVisible = mapCtx?.setIsMapVisible;
  const zoomToMapArea = useMemo(() => {
    if (!mapAreaFilter || !setIsMapVisible) return undefined;
    return () => {
      setIsMapVisible(true);
      const extent = getExtentFromGeoJson(mapAreaFilter as never);
      if (!extent || !mapView) return;
      void mapView.goTo(extent, { duration: 500 }).catch(() => {});
    };
  }, [mapAreaFilter, mapView, setIsMapVisible]);

  useEffect(() => {
    if (!aiPickMode) return undefined;
    const onClick = (event: MouseEvent) => {
      const el = (event.target as HTMLElement | null)?.closest?.(
        '[data-ai-target]'
      );
      if (!el) return;
      event.preventDefault();
      event.stopPropagation();
      const formBaseId = el.getAttribute('data-ai-form');
      if (!formBaseId) return;
      const sectionSource = el.getAttribute('data-ai-section') || undefined;
      const label =
        el.getAttribute('data-ai-label') ||
        (sectionSource ? `${formBaseId} ${sectionSource}` : formBaseId);
      const selectionId = `${formBaseId}:${sectionSource ?? '*'}`;
      setRealtimeSelections((prev) => {
        if (
          prev.some(
            (sel) => sel.source === 'ICS Form' && sel.id === selectionId
          )
        ) {
          toast.info(`${label} is already selected`);
          return prev;
        }
        toast.success(`Added ${label} to PRATUS AI`);
        return [
          ...prev,
          {
            source: 'ICS Form',
            id: selectionId,
            description: label,
            customData: {
              formBaseId,
              sectionSource,
              label,
              incidentId: incidentGuid,
            },
          },
        ];
      });
      setAiPickMode(false);
      setAiLayerPickMode?.(false);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [aiPickMode, incidentGuid, setAiLayerPickMode, setAiPickMode]);

  const toggleAiLayerPickMode = useMemo(() => {
    return () => {
      const next = !aiLayerPickMode;
      if (areaSelectIntentRef.current) {
        cancelAiAreaDraw?.();
        endAreaSelect();
      }
      setAiPickMode(next);
      setAiLayerPickMode?.(next);
      if (next) {
        toast.info('Click a map layer, form card, or form box — Esc to stop');
        try {
          if (!localStorage.getItem('pratus-ai-form-pick-hint')) {
            localStorage.setItem('pratus-ai-form-pick-hint', '1');
            toast.info(
              'ICS cards and boxes are selectable while this tool is on'
            );
          }
        } catch {
          // ignore private-mode storage failures
        }
      }
    };
  }, [
    aiLayerPickMode,
    cancelAiAreaDraw,
    endAreaSelect,
    setAiLayerPickMode,
    setAiPickMode,
  ]);

  useEffect(() => {
    if (!queuedComposerPrompt) return;
    setInput(queuedComposerPrompt);
    consumeQueuedComposerPrompt();
    setComposerFocusNonce((n) => n + 1);
  }, [queuedComposerPrompt, consumeQueuedComposerPrompt]);

  // Keep latest messages available to async sendMessage without re-creating it.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const sendMessage = useCallback(
    async (
      message: string,
      newChatName = '',
      prePromptData: RealtimeSelection[] | null = null,
      messageAttachments: ChatAttachment[] = []
    ) => {
      let data: RealtimeSelection[] =
        prePromptData !== null ? prePromptData : realtimeSelections;

      data = data.map((sel) =>
        hasLayers(sel)
          ? {
              ...sel,
              selectedLayers: withLegendClassFiltersForAI(
                sel.selectedLayers ?? [],
                legendClassFilterByLayerId ?? {}
              ),
            }
          : sel
      );

      if (
        notifyComposerSend(message, {
          selections: compactDisasterChatPayload({
            realtimeSelections: data,
            messages: [],
          }).realtimeSelections as unknown[],
          threadId: dchat.id ?? null,
        })
      ) {
        setInput('');
        return;
      }
      const finalNewChatName =
        newChatName ||
        message.slice(0, 50) ||
        (messageAttachments.length > 0 ? 'Image chat' : '');
      const withAttachments =
        messageAttachments.length > 0
          ? { attachments: messageAttachments }
          : {};

      dispatch(setStatus('loading'));
      setPriorStatus('Loading layer data…');
      const optimisticHistorySelections = compactDisasterChatPayload({
        realtimeSelections: data,
        messages: [],
      }).historyRealtimeSelections as RealtimeSelection[];
      const sendTimestamp = new Date();
      const priorMessages = messagesRef.current;
      setMessages([
        ...priorMessages,
        {
          role: 'USER',
          content: message,
          timestamp: sendTimestamp,
          realtimeSelections: optimisticHistorySelections,
          selectedDatetime,
          ...withAttachments,
        },
      ]);
      setInput('');
      setSendMessageTimestamp(sendTimestamp);

      try {
        data = await Promise.all(
          data.map(async (sel) => {
            if (!hasLayers(sel)) return sel;
            const withSnapshots = await prefetchLayerFeaturesForAI(
              sel.selectedLayers ?? [],
              {
                timeFiltersByLayerId: externalLayerTimeFilterById,
                getAddedExternalLayers,
              }
            );
            return {
              ...sel,
              selectedLayers: withArcGisUserTokensForAI(withSnapshots, {
                getAddedExternalLayers,
              }),
            };
          })
        );
      } catch (error) {
        observability.captureException(error, {
          where: 'chatV2.sendMessage.prefetch',
        });
      }

      const compacted = compactDisasterChatPayload({
        realtimeSelections: data,
        messages: priorMessages,
      });

      await postDChat({
        dchatId: dchat.id,
        message,
        realtimeSelections: compacted.realtimeSelections,
        selectedDatetime,
        newChatName: finalNewChatName,
        tenantId,
        sharepointDriveId,
        messages: compacted.messages,
        mode,
        ...withAttachments,
      });
    },
    [
      realtimeSelections,
      dchat.id,
      selectedDatetime,
      tenantId,
      sharepointDriveId,
      postDChat,
      mode,
      externalLayerTimeFilterById,
      getAddedExternalLayers,
      legendClassFilterByLayerId,
      dispatch,
      notifyComposerSend,
    ]
  );

  const selectionsValid = useMemo(
    () => areSelectionsValid(realtimeSelections),
    [realtimeSelections]
  );
  const readyAttachments = attachments.flatMap((item) =>
    item.status === 'ready' && item.attachment ? [item.attachment] : []
  );
  const attachmentsUploading = attachments.some(
    (item) => item.status === 'uploading'
  );
  const canSubmit =
    selectionsValid &&
    (input.trim().length > 0 || readyAttachments.length > 0) &&
    !attachmentsUploading &&
    !isAgentRunning;

  const submit = useCallback(() => {
    if (!canSubmit) return;
    void sendMessage(
      expandSlashCommand(input, commands),
      '',
      null,
      readyAttachments
    );
    attachmentsRef.current.forEach(revokePreview);
    setAttachments([]);
  }, [canSubmit, input, commands, sendMessage, readyAttachments]);

  // Mode follows the persisted chat mode when (re)opening a chat.
  useEffect(() => {
    if (dchat?.id && dchat.mode) {
      setMode(dchat.mode);
    }
  }, [dchat?.id, dchat?.mode]);

  // Resume polling only for a chat already in flight on the server.
  useEffect(() => {
    if (!dchat?.id) return undefined;
    if (dchat.status && SERVER_IN_PROGRESS_STATUSES.has(dchat.status)) {
      pollDChat(dchat.id);
    }
    return () => {
      stopPollDChat();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebind on chat switch only
  }, [dchat?.id, pollDChat, stopPollDChat]);

  // Stage label shown while the run is in progress.
  useEffect(() => {
    if (!dchat || Object.keys(dchat).length === 0) return;
    const s = dchat.status ?? '';
    if (s !== 'Complete' && s !== 'Error') {
      setPriorStatus(s);
    } else if (
      (s === 'Complete' && priorStatus !== 'Complete') ||
      (s === 'Error' && priorStatus !== 'Error')
    ) {
      setPriorStatus('Initializing');
    } else {
      setPriorStatus(s);
    }
  }, [dchat, priorStatus]);

  // Sync server messages (+ live stream bubble) into local optimistic list.
  useEffect(() => {
    if (dchat?.messages) {
      let shown: ChatMessage[] = [...dchat.messages];
      if (
        streamtext &&
        (status === 'loading' ||
          status === 'ongoing' ||
          status === 'pending_confirmation')
      ) {
        shown = [...shown, { role: 'ASSISTANT', content: streamtext }];
      }
      setMessages(shown);
    }
    if (!dchat || Object.keys(dchat).length === 0) {
      setMessages([]);
      // New / cleared session: allow hub and AOR defaults to apply again.
      hubDefaultsSeededRef.current = false;
      aorFilesSeededForRef.current = null;
      if (isHub) {
        setRealtimeSelections(defaultHubRealtimeSelections(highSecurity));
        hubDefaultsSeededRef.current = true;
      }
    }
    if (dchat?.messages?.length) {
      const last = dchat.messages[dchat.messages.length - 1];
      setRealtimeSelections(last.realtimeSelections ?? []);
      hubDefaultsSeededRef.current = true;
      aorFilesSeededForRef.current = incident?.id ?? null;
    }
  }, [dchat, streamtext, status, isHub, highSecurity, incident?.id]);

  // Seed the Files source once per new incident-workspace chat with the org
  // folders tagged for the incident's AORs. Adds to, never replaces, the
  // picker, and never touches a restored chat.
  useEffect(() => {
    if (isHub || !incident?.id) return;
    if (aorFilesSeededForRef.current === incident.id) return;
    const hasMessages =
      messages.length > 0 || (dchat?.messages?.length ?? 0) > 0;
    if (hasMessages) {
      aorFilesSeededForRef.current = incident.id;
      return;
    }
    if (!incidentAorIds?.length) {
      aorFilesSeededForRef.current = incident.id;
      return;
    }
    if (aorFileIds === null) return;
    aorFilesSeededForRef.current = incident.id;
    if (aorFileIds.length > 0) {
      setRealtimeSelections((prev) => withDefaultFiles(prev, aorFileIds));
    }
  }, [
    isHub,
    incident?.id,
    incidentAorIds,
    aorFileIds,
    messages.length,
    dchat?.messages?.length,
  ]);

  // Seed Web + weather sources once on an empty hub chat. Skip IAP and never
  // overwrite a restored chat or a user who already changed the picker.
  useEffect(() => {
    if (!isHub) return;
    const hasMessages =
      messages.length > 0 || (dchat?.messages?.length ?? 0) > 0;
    if (hasMessages) {
      hubDefaultsSeededRef.current = true;
      return;
    }
    if (hubDefaultsSeededRef.current) return;
    if (realtimeSelections.length > 0) {
      hubDefaultsSeededRef.current = true;
      return;
    }
    setRealtimeSelections(defaultHubRealtimeSelections(highSecurity));
    hubDefaultsSeededRef.current = true;
  }, [
    isHub,
    highSecurity,
    messages.length,
    dchat?.messages?.length,
    realtimeSelections.length,
  ]);

  const pendingConfirmations = useMemo(
    () =>
      dchat?.status === 'PendingConfirmation' &&
      Array.isArray(dchat?.pending_confirmation)
        ? dchat.pending_confirmation
        : [],
    [dchat?.status, dchat?.pending_confirmation]
  );
  const confirmationAskText =
    pendingConfirmations.length > 0 && streamtext ? streamtext : undefined;

  const submitConfirmations = useCallback(
    async (decisions: ResumeDecision[]): Promise<boolean> => {
      const ok = await dispatch(
        resumeDChat(dchat.id, decisions) as ThunkLike<Promise<boolean>>
      );
      return ok !== false;
    },
    [dispatch, dchat.id]
  );

  const cancel = useCallback(() => {
    void dispatch(cancelRun(dchat) as ThunkLike<Promise<void>>);
  }, [dispatch, dchat]);

  const close = useCallback(() => {
    dispatch(setSelectedDChat({}));
    dispatch(setCurrentSitrepId(undefined));
    onClose();
  }, [dispatch, onClose]);

  const deleteSession = useCallback(() => {
    if (!dchat.id) return;
    // useDeleteDisasterChat is untyped JS; its variables type infers as void.
    (deleteDisasterChat as unknown as { mutate: (id: string) => void }).mutate(
      dchat.id
    );
    close();
  }, [dchat.id, deleteDisasterChat, close]);

  const applyBlueprint = useCallback(
    (blueprint: ChatBlueprint) => {
      const structured = `# ${blueprint.title}\n\n${blueprint.description}`;
      let promptData: RealtimeSelection[] = realtimeSelections;
      if (blueprint.settings?.datasources) {
        setRealtimeSelections(blueprint.settings.datasources);
        promptData = blueprint.settings.datasources;
      }
      void sendMessage(structured, blueprint.title, promptData);
    },
    [realtimeSelections, sendMessage]
  );

  useEffect(() => {
    if (dchat && dchat.initialBlueprint && !dchat.id && !messages.length) {
      applyBlueprint(dchat.initialBlueprint);
    }
  }, [dchat, messages, applyBlueprint]);

  const redoMessage = useCallback(
    (assistantMessageIndex: number) => {
      const userMessage = messages[assistantMessageIndex - 1];
      if (!userMessage || userMessage.role !== 'USER') return;
      const trimmed = messages.slice(0, assistantMessageIndex - 1);
      setMessages(trimmed);

      void postDChat({
        dchatId: dchat.id,
        message: userMessage.content,
        realtimeSelections: userMessage.realtimeSelections,
        selectedDatetime,
        newChatName: '',
        tenantId,
        sharepointDriveId,
        messages: trimmed,
        mode,
      });

      const now = new Date();
      setMessages([
        ...trimmed,
        {
          role: 'USER',
          content: userMessage.content,
          timestamp: now,
          realtimeSelections: userMessage.realtimeSelections,
          selectedDatetime,
        },
      ]);
      setSendMessageTimestamp(now);
    },
    [
      messages,
      postDChat,
      dchat.id,
      selectedDatetime,
      tenantId,
      sharepointDriveId,
      mode,
    ]
  );

  return {
    dchat,
    messages,
    status,
    streamtext,
    isAgentRunning,
    priorStatus,
    sendMessageTimestamp,
    input,
    setInput,
    composerFocusNonce,
    submit,
    canSubmit,
    attachments,
    attachmentIndexStates,
    addFiles,
    removeAttachment,
    groupGuid,
    incidentId: incident?.id,
    selectionsValid,
    mode,
    setMode,
    commands,
    realtimeSelections,
    setRealtimeSelections,
    aiLayerPickMode,
    toggleAiLayerPickMode,
    aiAreaSelectActive,
    startAiAreaSelect,
    zoomToMapArea,
    sendMessage,
    redoMessage,
    cancel,
    deleteSession,
    close,
    pendingConfirmations,
    confirmationAskText,
    submitConfirmations,
  };
};
